/**
 * "Columns" popover for the Asset table — one row per valuation source
 * found across the job's assets, each with an Eye / EyeOff visibility
 * toggle (MOR-544). Mirrors the ColumnVisibilityMenu popover chrome but
 * uses eye icons (the API-key show/hide affordance) because visibility is
 * shared per job, not a personal table setting.
 */

import { useState } from "react";
import { Columns3, Eye, EyeOff } from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { cn } from "@/utils/misc";
import { formatSourceType } from "../helpers";

export function ValuationColumnsMenu({
  sources,
  visibleSources,
  onToggle,
  disabled,
}: {
  /** Every distinct valuation sourceType on the job, alphabetical. */
  sources: string[];
  /** Currently visible subset (already intersected with `sources`). */
  visibleSources: string[];
  onToggle: (sourceType: string) => void;
  /** Viewers can open the menu but not change the shared view. */
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const visible = new Set(visibleSources);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-2.5 py-1 text-xs text-muted-foreground transition hover:bg-secondary hover:text-foreground"
        >
          <Columns3 className="size-3.5" />
          Columns
          {visibleSources.length > 0 && (
            <Badge variant="secondary" className="ml-0.5 text-[10px]">
              {visibleSources.length}
            </Badge>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-1">
        {sources.length === 0 ? (
          <p className="px-2 py-3 text-xs text-muted-foreground">
            No valuations on this job yet. Sources appear here as valuations are
            extracted or added.
          </p>
        ) : (
          sources.map((source) => {
            const isVisible = visible.has(source);
            return (
              <button
                key={source}
                type="button"
                disabled={disabled}
                onClick={() => onToggle(source)}
                title={
                  disabled
                    ? "Viewers can't change the shared column view"
                    : isVisible
                      ? "Hide column"
                      : "Show column"
                }
                className={cn(
                  "flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-sm transition hover:bg-secondary",
                  disabled && "cursor-not-allowed opacity-60",
                )}
              >
                <span className="truncate text-primary/80">
                  {formatSourceType(source)}
                </span>
                {isVisible ? (
                  <Eye className="size-4 shrink-0 text-primary" />
                ) : (
                  <EyeOff className="size-4 shrink-0 text-muted-foreground/50" />
                )}
              </button>
            );
          })
        )}
      </PopoverContent>
    </Popover>
  );
}
